import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';

import { loadState, saveState } from '@elab/core/utils/storage';

import createReducer from './reducers';
import sagas from './saga';
import globalActions from './global/actions';
import * as configActions from './config/actions';
import { globalStateKey, store } from './store';
import { history } from './history';
import { getCurrentGlobalState } from './global-state';
import './dom-events';

const handleVisit = payload => store() && store().dispatch(globalActions.visited(payload));
const handleBeforeRender = payload =>
    store() && store().dispatch(globalActions.beforeRender(payload));

const handleLoad = payload => {
    const hardReload = document.head.querySelector('[data-turbolinks-track="reload"]');
    hardReload && window.scrollTo(0, 0);
    store() && store().dispatch(globalActions.pageLoad(payload));
};

export function initStore(featureConfigs, customSagas, customReducers) {
    if (!store()) {
        // prettier-ignore
        // eslint-disable-next-line prettier/prettier
        const isIE = /* @cc_on!@ */false || !!document.documentMode; // Internet Explorer 6-11
        const isEdge = !isIE && !!window.StyleMedia; // Edge 20+
        let persistedState = {};
        if (isIE || isEdge) {
            persistedState = loadState();
            window.addEventListener('beforeunload', () => {
                saveState(store().getState());
            });
        }

        const sagaMiddleware = createSagaMiddleware();
        const middleware = applyMiddleware(sagaMiddleware);
        const globalState = (global[globalStateKey] = {});
        globalState.history = history;
        const reducers = customReducers || createReducer;
        globalState.store = createStore(
            reducers({}),
            persistedState,
            window.__feDebugMode ? composeWithDevTools(middleware) : middleware,
        );

        globalState.store.dynamicReducers = {};
        globalState.store.dynamicSagas = {};
        sagaMiddleware.run(customSagas || sagas);

        globalState.store.dispatch(configActions.setConfigs(featureConfigs));

        // Patch Turbolinks to render 403, 404 & 500 normally
        // See https://github.com/turbolinks/turbolinks/issues/179
        window.Turbolinks.HttpRequest.prototype.requestLoaded = function() {
            return this.endRequest(
                function() {
                    const code = this.xhr.status;
                    const contentType = this.xhr.getResponseHeader('Content-Type');
                    const contentTypeIsHTML = contentType => {
                        return (contentType || '').match(/^text\/html|^application\/xhtml\+xml/);
                    };
                    if (contentTypeIsHTML(contentType)) {
                        if (
                            (code >= 200 && code < 300) ||
                            code === 403 ||
                            code === 404 ||
                            code === 500
                        ) {
                            this.delegate.requestCompletedWithResponse(
                                this.xhr.responseText,
                                this.xhr.getResponseHeader('Turbolinks-Location'),
                            );
                        } else {
                            this.failed = true;
                            this.delegate.requestFailedWithStatusCode(code, this.xhr.responseText);
                        }
                    } else {
                        this.failed = true;
                        this.delegate.requestFailedWithStatusCode(-2);
                    }
                }.bind(this),
            );
        };

        window.dispatchEvent(new Event('resize'));
        if (!isIE && !isEdge) {
            // Turbolinks are not enabled for IE and Edge
            document.addEventListener('turbolinks:visit', handleVisit);
            document.addEventListener('turbolinks:before-render', handleBeforeRender);
            document.addEventListener('turbolinks:load', handleLoad);
        } else {
            document.addEventListener('DOMContentLoaded', e => {
                handleBeforeRender(e);
                handleLoad(e);
            });
        }
    }

    return Promise.resolve(store());
}

export { store, getCurrentGlobalState };
export default store;
